import * as React from 'react'
import { css, Rule } from 'glamor'
import { omit } from 'lodash-es'
import * as PropTypes from 'prop-types'
import { compose, mapProps, withState } from 'recompose'

import { IInputProps } from '../../types'

import getStyles from './styles'
import { useTheme } from '../../theme'

export interface IRadioProps {
  onClick?: () => void
  checked?: boolean
  disabled?: boolean
  style?: Rule
  input?: IInputProps<React.ChangeEvent<HTMLInputElement>>
  block?: boolean
  label?: React.ReactNode
  flush?: boolean
  required?: boolean
}

export function Radio({
  checked,
  input,
  disabled,
  style,
  block,
  onClick,
  label,
  flush,
  ...additionalProps
}: IRadioProps & IInputProps<React.ChangeEvent<HTMLInputElement>>) {
  const styles = getStyles(useTheme())
  const inputProps = omit(
    {
      ...css([styles.input.base, disabled && styles.input.disabled]),
      type: 'radio',
      checked,
      disabled,
      ...input,
      ...additionalProps,
    },
    ['meta', 'hasErrored']
  )
  return (
    // keyboard navigation is accomplished with the input element focus
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <span
      {...css([
        styles.wrapper.base,
        !disabled && styles.wrapper.normal,
        block && styles.wrapper.block,
        flush && styles.wrapper.flush,
        style,
      ])}
      onClick={onClick}
    >
      <RadioIcon checked={inputProps.checked} disabled={inputProps.disabled} hasLabel={label != null} />
      <input {...inputProps} readOnly={!inputProps.onChange} />
      {label && <span>{label}</span>}
    </span>
  )
}

Radio.propTypes = {
  onClick: PropTypes.func,
  checked: PropTypes.bool,
  disabled: PropTypes.bool,
  style: PropTypes.object,
  input: PropTypes.object,
  block: PropTypes.bool,
  label: PropTypes.node,
  flush: PropTypes.bool,
}

interface IRadioIconProps {
  children?: React.ReactNode
  checked?: boolean
  disabled?: boolean
  hasLabel?: boolean

  hovered?: boolean
  setHovered?: (state: boolean) => void
  onMouseOver?: (event: React.MouseEvent) => void
  onMouseOut?: (event: React.MouseEvent) => void
}

const enhanceRadioIcon = compose<IRadioIconProps, IRadioIconProps>(
  withState('hovered', 'setHovered', false),
  mapProps(({ setHovered, onMouseOver, onMouseOut, ...additional }: IRadioIconProps) => ({
    onMouseOver: (event: React.MouseEvent) => {
      if (setHovered) setHovered(true)
      if (onMouseOver) onMouseOver(event)
    },
    onMouseOut: (event: React.MouseEvent) => {
      if (setHovered) setHovered(false)
      if (onMouseOut) onMouseOut(event)
    },
    ...additional,
  }))
)

const RadioIcon = enhanceRadioIcon(
  ({ checked, disabled, hasLabel, hovered, onMouseOver, onMouseOut }: IRadioIconProps) => {
    const styles = getStyles(useTheme())

    const containerProps = {
      onMouseOver,
      onMouseOut,
    }

    const pathProps = css([
      !disabled && !hovered && styles.radioFillPath.normal,
      !disabled && hovered && styles.radioFillPath.hovered,
      disabled && styles.radioFillPath.disabled,
    ])

    const svgStyles = css([styles.svg.base, hasLabel && styles.svg.withLabel])

    if (!checked) {
      return (
        <svg
          {...containerProps}
          {...css(svgStyles)}
          height="20"
          viewBox="0 0 20 20"
          width="20"
          xmlns="http://www.w3.org/2000/svg"
        >
          <g fill="none" fillRule="evenodd">
            <path d="M0 0h20v20H0z" />
            <path
              d="M10 20c5.523 0 10-4.477 10-10S15.523 0 10 0 0 4.477 0 10s4.477 10 10 10zm0-1.667a8.333 8.333 0 1 0 0-16.666 8.333 8.333 0 0 0 0 16.666z"
              fill={hovered ? 'rgba(153, 153, 153, 0.4)' : 'rgba(182, 198, 214, 0.5)'}
            />
          </g>
        </svg>
      )
    }

    return (
      <svg
        {...containerProps}
        {...css(svgStyles)}
        height="20"
        viewBox="0 0 20 20"
        width="20"
        xmlns="http://www.w3.org/2000/svg"
      >
        <g fill="none" fillRule="evenodd">
          <path d="M0 0h20v20H0z" />
          <path
            {...pathProps}
            d="M10 20c5.523 0 10-4.477 10-10S15.523 0 10 0 0 4.477 0 10s4.477 10 10 10zm0-1.667a8.333 8.333 0 1 0 0-16.666 8.333 8.333 0 0 0 0 16.666z"
            fill="#298BFF"
          />
          <circle {...pathProps} cx="10" cy="10" r="4" />
        </g>
      </svg>
    )
  }
)
